import { Injectable } from '@angular/core';
import { Project, ProjectDedupeStatus, ProjectTeam, ProjectTag, ProjectSurveyPartner, PartnerList, ProjectTeamMembersForPartner, ProjectTestId, TrackerWave } from '../models/project';
import { ProjectFlagState } from '../models/project-flag-state';
import { VendorCpiChange, VendorCpiChangeResult, VendorCpiTiers } from '../models/vendor-cpi-change';
import { AuthService } from './auth.service';
import { ProjectListView } from '../models/project-list-view';
import { ProjectType } from '../models/project-type';
import { UtilsService } from '../services/utils.service';
import { environment } from '../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { DecipherTermListDto, OptionValue } from '../models/DecipherTermDto';
import { DecipherDropoutListDto } from '../models/DecipherDropoutDto';
import {
  FamilySurveyLinkItem,
  FamilySurveyLinkWrite,
  FamilySurveyLinksSaveResponse,
} from '../models/family-survey-links';
import {
  FamilyVendorRedirectItem,
  FamilyVendorRedirectWrite,
  FamilyVendorRedirectsSaveResponse,
} from '../models/family-vendor-redirects';
import { parseHealthBreakdown } from '../models/health-breakdown';
import { buildEntryUrlParams } from '../helpers/vendor-url-params';
import { VendorUrlParams } from '../models/url-param-mapping';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  baseUrl = environment.apiEndpoint + '/project/';
  syntheticVendorId = null;

  constructor(private http: HttpClient, public utils: UtilsService, public auth: AuthService) {
    this.syntheticVendorId = this.auth.getSyntheticPanel()?.partnerId;
  }

  private switchSectionFromModalSubject = new Subject<void>();
  switchSectionFromModal$ = this.switchSectionFromModalSubject.asObservable();

  // Call this method to switch to project section from a modal
  switchSection(evt: any) {
    this.switchSectionFromModalSubject.next(evt);
  }


  GetProjects(settings: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'list', settings);
  }
  GetProjectFlags(projectId: string): Observable<ProjectFlagState[]> {
    return this.http.get<ProjectFlagState[]>(this.baseUrl + projectId + '/flags');
  }
  EnrichProjects(projects) {
    // Changes some of the data we get from the DB so that is more useful on angular side
    projects.forEach(project => this.EnrichProject(project));

    return projects;
  }
  EnrichProject(project) {

    // Changes some of the data we get from the DB so that is more useful on angular side
    project.pm = (project.projectManager == null)
      ? project.projectTeam === undefined
        ? null
        : project.projectTeam.find(x => x.projectRole == 'PM') !== undefined
          ? project.projectTeam.find(x => x.projectRole == 'PM').displayName
          : null
      : [{ name: project.projectManager, type: 'pm' }];

    project.pmEmail = project.projectTeam?.find(x => x.projectRole == 'PM')?.email;
    project.secondPm = project.secondaryManagers?.split(",") || []
    project.oaList = project.operationsAssociates?.split(",") || []

    project.accountOwner = (project.accountOwner == null)
      ? []
      : [{ name: project.accountOwner, type: 'accountOwner' }];

    if (project?.vendorPms == null || project?.vendorPms?.length == 0) {
      project.vendorPms = [];
    }
    else {
      project.vendorPms = project.vendorPms?.split(',').map((e) => ({ name: e }));
    }

    project.totalDays = this.utils.daysBetween(new Date(project.fieldStartDate), new Date(project.fieldEndDate));
    project.totalDaysLeft = this.utils.daysBetween(new Date(), new Date(project.fieldEndDate));
    // Days elapsed since field opened. Derived here alongside the other field-date figures
    // because it was previously computed only inside the homepage survey-performance card,
    // so anything else reading it got undefined. A finished project measures to its close,
    // so its observed rates aren't diluted by every day since field ended.
    if (project.fieldStartDate == null) {
      project.totalDaysInField = 0;
    } else {
      const finished = ['Closed', 'Invoiced'].includes(project.projectStatus);
      const measuredTo = finished
        ? project.fieldEndDate ?? project.closedDateTime ?? new Date()
        : new Date();
      project.totalDaysInField = this.utils.daysBetween(
        new Date(project.fieldStartDate),
        new Date(measuredTo)
      );
    }
    project.percentageDaysLeft = Math.ceil((project.totalDaysLeft / project.totalDays) * 100);
    if (project.percentageDaysLeft < 0) {
      project.percentageDaysLeft = 0;
    }
    if (project.percentageDaysLeft > 100) {
      project.percentageDaysLeft = 100;
    }
    // List payloads carry the count as `complete`, by-id payloads as `completes`.
    project.percentageComplete = ((project.complete ?? project.completes) * 100) / project.fullLaunchQuota;

    //Update GUIDS to be uppercase
    if (project.clientId != null) {
      project.clientId = project.clientId.toLowerCase();
    }
    project.projectSegments?.forEach(seg => {
      seg.projectSurveyPartners.forEach(partner => {
        partner.partnerId = partner.partnerId.toLowerCase();
      })
    });
    project = this.HandleHealthDetails(project);
    project._countryBadges = this.GetCountryBadges(project);
    // Display value for the list views' Frequency column: the tracker's frequency, or Ad-hoc.
    project._frequency = project.isTracker ? (project.trackerFrequency || 'Tracker') : 'Ad-hoc';

    return project;
  }

  // Geo restriction is a single per-project toggle covering every market - there is no per-country
  // subset, so the badges here are just the codes. Restriction status is shown once at the row/card
  // level (isFraudDetectionGeoCountryEnabled), not repeated per chip.
  GetCountryBadges(project) {
    return (project.countries || []).map(code => ({ code }));
  }

  // One health icon: colour from the score band, hover text from the lambda's
  // HealthBreakdown (NK-446). The table and both cards render health_icons[0].
  HandleHealthDetails(project) {
    let icon = 'far fa-triangle-exclamation nav-error';
    if (project.health <= 3) {
      icon = 'far fa-square-check nav-success';
    } else if (project.health <= 8) {
      icon = 'far fa-triangle-exclamation nav-medium-risk';
    }
    project.health_icons = [{ icon, message: this.DescribeHealthBreakdown(project.healthBreakdown) }];

    // Expected IR lives on the segment; other cards read it off the project.
    project.ir = (project.projectSegments == null) ? project.ir : project.projectSegments[0].projectIR;
    return project;
  }

  // One line per scored item, e.g. "Pace (Alert)" or "CID fraud rate: 12% vs 10% expected".
  DescribeHealthBreakdown(json: string): string {
    const breakdown = parseHealthBreakdown(json);
    if (!breakdown) return '';
    if (breakdown.items.length === 0) {
      return breakdown.reason === 'belowMinStarts' ? 'Not enough starts to score yet' : 'No issues';
    }
    return breakdown.items.map(item => {
      let line = item.label;
      if (item.state) line += ` (${item.state})`;
      if (item.actual != null && item.expected != null) line += `: ${item.actual}% vs ${item.expected}% expected`;
      return line;
    }).join('\n');
  }

  GetProjectTypes(instanceId?: string, cache?: boolean, loadAll?: boolean, vendorId?: string, clientId?: string): Observable<ProjectType[]> {

    if (vendorId === undefined) {
      vendorId = null;
    }
    if (clientId === undefined) {
      clientId = null;
    }
    if (loadAll === undefined) {
      loadAll = null;
    }

    let url = this.baseUrl + "GetProjectTypes";;
    let params = new HttpParams();

    if (instanceId !== null) {
      params = params.set('instanceId', instanceId);
    }
    if (loadAll !== null) {
      params = params.set('loadAll', loadAll);
    }
    if (vendorId !== null) {
      params = params.set('vendorId', vendorId);
    }
    if (clientId !== null) {
      params = params.set('clientId', clientId);
    }
    if (cache) {
      return this.http.get<ProjectType[]>(url, { headers: { cache: '1' }, params });
    }
    else {
      return this.http.get<ProjectType[]>(url, { params });
    }
  }

  SaveProjectType(instanceId: string, details: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + 'types/' + instanceId, details);
  }
  DeleteProjectType(typeId: string) {
    return this.http.delete<any>(this.baseUrl + 'types/' + typeId);
  }
  GetProjectTypeDetails(type: string) {
    return this.http.get<any>(this.baseUrl + 'type/' + type);
  }

  GetProjectListViews(instanceId?: string, cache?: boolean): Observable<ProjectListView[]> {
    if (cache) {
      return this.http.get<ProjectListView[]>(this.baseUrl + 'listviews/' + (instanceId ?? ''), { headers: { cache: '1' } });
    }
    else {
      return this.http.get<ProjectListView[]>(this.baseUrl + 'listviews/' + (instanceId ?? ''));
    }
  }

  ExportGroupProjectList(columns, projects, fileName) {
    let csvContent = '';

    // Colum headers
    columns.forEach((col: any) => {
      if (col.type === 'img' || col.type === 'actions-inline' || col.type === 'actions-dropdown' || col.type === 'actions-checkbox') { return true; }

      csvContent += col.label + ',';
    });
    csvContent += '\n';

    // Data
    projects.forEach((row: any) => {
      columns.forEach((col: any) => {
        if (col.type === 'img' || col.type === 'actions-inline' || col.type === 'actions-dropdown' || col.type === 'actions-checkbox') { return true; }

        if (row[col.id] == null) {
          csvContent += ',';
        }
        else if (col.type === 'tag') {
          csvContent += row[col.id].label + ',';
        } else if (col.type === 'address') {
          csvContent += (row[col.id].address1 ?? '') + ',';
        } else if (col.type === 'text') {
          csvContent += '"' + ((row[col.id] ?? '') + '",');
        } else if (col.type === 'users') {
          csvContent += ((row[col.id] != null && row[col.id].length > 0 && row[col.id][0].name != null) ? row[col.id][0]?.name : '') + ',';
        } else if (col.type === 'tags') {
          let tags = row[col.id].map(tag => tag.tag).join('|');
          csvContent += tags + ',';
        }
        else if (col.type === 'cleanID') {
          csvContent += '"' + ((this.cleanIDImplementation(row, col) ?? '') + '",');
        } else {
          csvContent += (row[col.id] ?? '') + ',';
        }
      });
      csvContent += '\n';
    });

    this.utils.generateXLSX(csvContent, fileName);
  }

  cleanIDImplementation(item, col): string {
    if (item.cleanIDEnable == false) {
      return "Disabled"
    } else if (item.cleanIDEnable == true && item.cleanIDImplementation == null) {
      return "Enabled"
    }
    return item[col.id]
  }

  SaveProjectListView(instanceId: string, json: ProjectListView): Observable<ProjectListView> {
    return this.http.put<ProjectListView>(this.baseUrl + 'listviews/' + instanceId, json);
  }
  DeleteProjectListView(model: ProjectListView): Observable<any> {
    return this.http.delete(this.baseUrl + 'listviews/' + model.id);
  }
  UpdateProjectListViewRoles(viewId: string, roles: { id: string; name: string }[]): Observable<ProjectListView> {
    return this.http.put<ProjectListView>(this.baseUrl + 'listviews/' + viewId + '/roles', roles);
  }
  UpdateProjectTypePermissions(projectTypeId: string, permissions: any, instanceId?: string): Observable<any> {
    const query = instanceId ? '?instanceId=' + instanceId : '';
    return this.http.put<any>(this.baseUrl + 'types/' + projectTypeId + '/permissions' + query, permissions);
  }
  GetRelatedProjects(id: string) {
    return this.http.get<any>(this.baseUrl + id + '/related', { headers: { cache: '1' } });
  }
  GetOrderedRelatedProjects(id: string) {
    return this.http.get<any>(this.baseUrl + id + '/related/ordered', { headers: { cache: '1' } });
  }
  GetOrderedRelatedProjectsSummary(bidNumber: string) {
    return this.http.get<any>(this.baseUrl + encodeURI(bidNumber) + '/related/ordered/summary', { headers: { cache: '1' } });
  }
  /** Every wave with this tracker ID (each wave's parent project), latest first. */
  GetTrackerWaves(trackerId: string): Observable<TrackerWave[]> {
    return this.http.get<TrackerWave[]>(this.baseUrl + 'tracker/' + encodeURIComponent(trackerId) + '/waves', { headers: { cache: '1' } });
  }
  GetRelatedByBidNumber(id: string, bidNumber: string) {
    return this.http.get<any>(this.baseUrl + id + '/relatedbybid/' + encodeURI(bidNumber), { headers: { cache: '0' } });
  }
  GetProject(id: string, vendorId?: string): Observable<Project> {
    return this.http.get<Project>(this.baseUrl + id + "/" + vendorId);
  }
  // NK-462. Realtime QC Flow is only offered where the project resolves to a Decipher API key.
  GetDecipherKeyStatus(projectId: string): Observable<{ hasDecipherKey: boolean }> {
    return this.http.get<{ hasDecipherKey: boolean }>(this.baseUrl + projectId + '/decipherkeystatus');
  }

  GetProjectForLaunchConfirmation(id: string) {
    return this.http.get<any>(this.baseUrl + id + '/launch');
  }
  GetProjectTermData(id: string) {
    return this.http.get<DecipherTermListDto>(this.baseUrl + id + '/decipherdata');
  }

  // NK-223. Companion to GetProjectTermData for the Survey Data card's Dropouts status.
  GetProjectDropoutData(id: string): Observable<DecipherDropoutListDto> {
    return this.http.get<DecipherDropoutListDto>(this.baseUrl + id + '/decipherdropoutdata');
  }

  GetDecipherVariableValues(id: string, variableName: string) {
    return this.http.get<OptionValue[]>(this.baseUrl + id + '/decipherdatavariablevalues?variableName='+variableName);
  }

  // GetProject(id: string, vendorId?: string, clientId?: string): Observable<Project> {
  //   let url = this.baseUrl;
  //   url = this.baseUrl + "getproject";
  //   let params = new HttpParams();
  //   if (id !== '') {
  //     params = params.set('id', id);
  //   }
  //   if (vendorId !== '') {
  //     params = params.set('vendorId', vendorId);
  //   }
  //   if (clientId !== '') {
  //     params = params.set('clientId', clientId);
  //   }
  //   return this.http.get<Project>(url, { params });
  // }

  DeleteProject(id: string) {
    return this.http.delete<any>(this.baseUrl + id);
  }

  CreateProject(model: any): Observable<Project> {
    return this.http.post<Project>(this.baseUrl, model);
  }

  // Wave (tracker) clone — clones every project sharing the source's bid
  // number in one synchronous call, which can be slow for large trackers.
  CloneTracker(model: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'clonetracker', model);
  }
  SaveProject(projectId, model: any): Observable<Project> {
    return this.http.put<Project>(this.baseUrl + projectId, model);
  }
  GetEntryId(project, segment, partner): Observable<string> {
    return this.http.get<string>(this.baseUrl + 'entryid/' + project + '/' + segment + '/' + partner);
  }

  UpdateProjectValue(projectId: string, fieldId: string, value: any, forceString = false): Observable<any> {
    const field: any = { fieldId };
    if (typeof value === 'boolean') {
      field.bVal = value;
    } else if (typeof value === 'string') {
      field.sVal = value;
      if (!forceString && !isNaN(field.sVal) && field.sVal !== '') {
        field.nVal = parseInt(field.sVal);
        field.fVal = field.nVal;
      }
    } else if (typeof value === 'number') {
      if (Number.isInteger(value)) field.nVal = value;
      field.fVal = value;
    } else if (value instanceof Date) {
      field.dVal = value;
    } else if (value instanceof Array) {
      if (value.every(i => typeof i === 'string')) {
        field.sVal3 = value;
      } else {
        field.lVal = value;
      }

    }
    return this.http.put<any>(this.baseUrl + projectId + '/field', field);
  }
  UpdateProjectTeam(projectId: string, projectTeam: ProjectTeam[]): Observable<any> {
    return this.http.put<any>(this.baseUrl + projectId + '/team', projectTeam);
  }
  UpdateProjectTags(projectId: string, projectTags: string[]): Observable<any> {
    return this.http.put<any>(this.baseUrl + projectId + '/tags', projectTags);
  }

  UpdateProjectDedupeStatus(projectId: string, statuses: ProjectDedupeStatus[]): Observable<any> {
    return this.http.put<any>(this.baseUrl + projectId + '/dedupestatus', statuses);
  }

  UpdateProjectStatus(id: string, value: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/status', { fieldId: '', sVal: value });
  }

  LaunchSurvey(id: string, statusChangeToLiveDetails: any) {
    return this.http.put<any>(this.baseUrl + id + '/launch', statusChangeToLiveDetails);
  }

  SaveGoLiveChecklistAttestation(id: string, attestation: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + id + '/golivechecklist', attestation);
  }

  // Two endpoints over the same table, at different grain, one per caller. NK-315's Go
  // Live Checklist is released and reads the grouped roll-up below; NK-269's Testing panel
  // needs one row per test respondent, which means no GROUP BY and a wider SurveyState
  // allow-list. Neither change is something the checklist can absorb — its `lastTestedOn`
  // only exists because of the grouping, and its four-state list is deliberate — so the
  // panel gets its own query rather than the released gate being edited to serve it.

  // Only vendors with test evidence come back — an absent vendor has not been tested.
  GetVendorTestActivity(id: string): Observable<{ partnerId: string, lastTestedOn: string }[]> {
    return this.http.get<{ partnerId: string, lastTestedOn: string }[]>(this.baseUrl + id + '/vendortestactivity');
  }

  // One row per test respondent captured while its vendor sat in Test field status.
  // A null partnerId is OpinionRoute's own test traffic, which reaches the survey
  // without a vendor attached. A vendor with no rows has not been tested.
  GetProjectTestIds(id: string): Observable<ProjectTestId[]> {
    return this.http.get<ProjectTestId[]>(this.baseUrl + id + '/vendortestids');
  }

  // Returns the platform saved (a link moved onto a Decipher host saves as Decipher) and whether
  // that move happened, which is when Realtime QC Flow is offered.
  UpdateProjectSurveyLinks(id: string, live: string, test: string, surveyPlatForm: string): Observable<{ surveyPlatForm: string, movedToDecipher: boolean }> {
    return this.http.put<{ surveyPlatForm: string, movedToDecipher: boolean }>(this.baseUrl + id + '/surveylinks', { surveyLink: live, testSurveyLink: test, surveyPlatForm: surveyPlatForm });
  }

  GetFamilySurveyLinks(bidNumber: string): Observable<FamilySurveyLinkItem[]> {
    return this.http.get<FamilySurveyLinkItem[]>(
      this.baseUrl + 'summary/' + encodeURIComponent(bidNumber) + '/survey-links');
  }

  SaveFamilySurveyLinks(bidNumber: string, items: FamilySurveyLinkWrite[]): Observable<FamilySurveyLinksSaveResponse> {
    return this.http.put<FamilySurveyLinksSaveResponse>(
      this.baseUrl + 'summary/' + encodeURIComponent(bidNumber) + '/survey-links', { items });
  }

  ListProjectPartners(id: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + id + '/partner');
  }
  DeletePartnerFromProject(id: string, partnerid: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + id + '/partner/' + partnerid);
  }
  SaveProjectPartner(id: string, partner: ProjectSurveyPartner, pms): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/partner', { partner: partner, pms: pms });
  }

  UpdateProjectPartnerStatus(id: string, partnerid: string, value: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/partner/' + partnerid + '/status', { fieldId: '', sVal: value });
  }
  // One call for a chosen set of vendors: a single Nav save and a single Survey Engine push.
  // Per-vendor calls in parallel each pushed the whole project to SE and overwrote each other.
  UpdateProjectPartnerStatuses(id: string, partnerIds: string[], value: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/partners/status', { partnerIds: partnerIds, status: value });
  }
  UpdateProjectPartnerCPI(id: string, partnerid: string, value: number): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/partner/' + partnerid + '/cpi', { fieldId: '', fVal: value });
  }
  // NK-351: applies a vendor CPI change that records which completes the new
  // rate covers, so delivered traffic can be repriced rather than inheriting
  // the new rate silently.
  ApplyProjectPartnerCPIChange(id: string, change: VendorCpiChange): Observable<VendorCpiChangeResult> {
    return this.http.put<VendorCpiChangeResult>(
      this.baseUrl + id + '/partner/' + change.partnerId + '/cpi-change', change);
  }

  // NK-351: the rates a vendor has run at, so the price change modal can preview
  // the split against what actually happened rather than one assumed earlier rate.
  GetProjectPartnerCPITiers(id: string, partnerId: string): Observable<VendorCpiTiers> {
    return this.http.get<VendorCpiTiers>(this.baseUrl + id + '/partner/' + partnerId + '/cpi-tiers');
  }

  UpdateProjectSegmentCPI(id: string, segmentid: string, value: number): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/segment/' + segmentid + '/cpi', { fieldId: '', fVal: value });
  }

  // NK-351: the client-side equivalents of the vendor CPI change pair. Client CPI is
  // revenue and hangs off a segment rather than a vendor, so the ids differ, but the
  // payload, the restamp and the derived rate history are the same shape.
  ApplyProjectSegmentCPIChange(id: string, change: VendorCpiChange): Observable<VendorCpiChangeResult> {
    return this.http.put<VendorCpiChangeResult>(
      this.baseUrl + id + '/segment/' + change.segmentId + '/cpi-change', change);
  }

  GetProjectSegmentCPITiers(id: string, segmentId: string): Observable<VendorCpiTiers> {
    return this.http.get<VendorCpiTiers>(this.baseUrl + id + '/segment/' + segmentId + '/cpi-tiers');
  }
  UpdateAllProjectPartnerStatus(id: string, value: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/status/partners', { fieldId: '', sVal: value });
  }
  UpdateProjectPartnerAllocation(id: string, partnerid: string, value: number, soft: boolean): Observable<any> {
    if (soft) {
      return this.http.put<any>(this.baseUrl + id + '/partner/' + partnerid + '/allocation/soft', { fieldId: '', nVal: value });
    } else {
      return this.http.put<any>(this.baseUrl + id + '/partner/' + partnerid + '/allocation/full', { fieldId: '', nVal: value });
    }
  }

  UpdateProjectPartnerAllocationEnforcement(id: string, partnerid: string, enforce: boolean): Observable<any> {
    return this.http.put<any>(this.baseUrl + id + '/partner/' + partnerid + '/enforce/' + enforce, {});
  }

  BulkUpdateProjectPartnerAllocationEnforcement(projectId: string, partnerIds: string[], enforce: boolean): Observable<any> {
    return this.http.put<any>(this.baseUrl + projectId + '/partner/enforce/' + enforce, partnerIds);
  }

  GetProjectParticipants(id, vendorId?: string, excludeSynthetic: boolean = false) {
    const tzoffset = new Date().getTimezoneOffset() * -1;
    return this.http.get<any>(this.baseUrl + id + '/participants?tzoffset=' + tzoffset + "&excludeSynthetic=" + excludeSynthetic + "&vendorId=" + vendorId);
  }

  GetFamilyParticipants(bidNumber, excludeSynthetic: boolean = false) {
    const tzoffset = new Date().getTimezoneOffset() * -1;
    return this.http.get<any>(this.baseUrl + 'summary/' + encodeURI(bidNumber) + '/participants?tzoffset=' + tzoffset + "&excludeSynthetic=" + excludeSynthetic);
  }

  GetFamilyVendors(bidNumber) {
    return this.http.get<PartnerList>(this.baseUrl + 'summary/' + encodeURI(bidNumber) + '/vendors');
  }

  GetBulkProjectUrls(listOfIds: Array<string>): Observable<any> {
    return this.http.post(this.baseUrl + 'urls', listOfIds);
  }

  UpdateQuestions(listOfQuestions: any[]): Observable<any> {
    return this.http.post(this.baseUrl + 'questions', listOfQuestions);
  }

  GetLocations(type: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'locations?type=' + type, { headers: { cache: '1' } });
  }

  DeleteProjectHealthConfig(id: string) {
    return this.http.delete<any>(this.baseUrl + 'healthconfig/' + id)
  }

  CreateProjectHealthConfig(instanceId: string, name: string, description: string, settings: string) {
    return this.http.post<any>(this.baseUrl + 'healthconfig', { instanceId: instanceId, configName: name, description: description, settings: settings });
  }

  GetAllProjectHealthConfigs(instanceId: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'healthconfig/' + instanceId, {});
  }

  UpdateProjectHealthConfig(id: string, name: string, description: string, settings: string) {
    return this.http.put<any>(this.baseUrl + 'healthconfig/' + id, { configName: name, description: description, settings: settings })
  }

  CreateProjectWithThirdParties(id) {
    return this.http.post<any>(this.baseUrl + id + '/connector', {});
  }

  GetProjectStatusWithThirdParties(id) {
    return this.http.get<any>(this.baseUrl + id + '/connector/status');
  }

  GlobalSearch(searchWord: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'globalsearch?searchWord=' + encodeURIComponent(searchWord));
  }

  CopySearch(searchWord: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'copysearch?searchWord=' + encodeURIComponent(searchWord));
  }

  SavePartnerProjectNumber(projectid, partnerid, projectNum): Observable<any> {
    const field: any = { fieldId: 'projectnumber', sVal: projectNum };
    return this.http.put<any>(this.baseUrl + projectid + '/partner/' + partnerid + '/projectnumber', field);
  }
  SavePartnerProjectPms(projectid, partnerid, pms): Observable<any> {
    return this.http.put<any>(this.baseUrl + projectid + '/partner/' + partnerid + '/pms', pms);
  }
  SavePartnerProjectRedirects(projectid, partnerid, links): Observable<any> {
    return this.http.put<any>(this.baseUrl + projectid + '/partner/' + partnerid + '/redirects', links);
  }
  // Returns the vendor's entry link, regenerated from the new mappings.
  SavePartnerProjectUrlParams(projectId: string, partnerId: string, params: VendorUrlParams): Observable<{ entryUrl: string }> {
    return this.http.put<{ entryUrl: string }>(this.baseUrl + projectId + '/partner/' + partnerId + '/urlparams', params);
  }

  GetFamilyVendorRedirects(bidNumber: string): Observable<FamilyVendorRedirectItem[]> {
    return this.http.get<FamilyVendorRedirectItem[]>(
      this.baseUrl + 'summary/' + encodeURIComponent(bidNumber) + '/vendor-redirects');
  }

  SaveFamilyVendorRedirects(bidNumber: string, items: FamilyVendorRedirectWrite[]): Observable<FamilyVendorRedirectsSaveResponse> {
    return this.http.put<FamilyVendorRedirectsSaveResponse>(
      this.baseUrl + 'summary/' + encodeURIComponent(bidNumber) + '/vendor-redirects', { items });
  }

  setPerformanceCardView(settings) {
    localStorage.setItem('performanceCardViewSettings', JSON.stringify(settings));
  }
  getPerformanceCardView() {
    const settings = localStorage.getItem('performanceCardViewSettings');
    return (settings != null) ? JSON.parse(settings) : { showPreSurveyBreakdown: false, showInSurveyBreakdown: true, showKPIBreakdown: false, sortOrder: 'desc' };
  }

  setCardDateOrder(settings, cardName) {
    localStorage.setItem(`${cardName}CardDateOrderSettings`, JSON.stringify(settings));
  }
  getCardDateOrder(cardName) {
    const settings = localStorage.getItem(`${cardName}CardDateOrderSettings`);
    return (settings != null) ? JSON.parse(settings) : {sortOrder: 'desc' };
  }

  generateEntryUrlParams(surveyLink: string, links: any) {
    return buildEntryUrlParams(surveyLink, links);
  }

  getVendorPricingForProject(projectId: string, vendorId?: string): Observable<any>
  {
    return this.http.get<any>(this.baseUrl + projectId + "/vendorpricing?vendorId=" + vendorId);
   }
  revertProjectStatusToClosedSuperAdmin(id: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + id + '/revertProjectStatusToClosed', {});
  }

  getMultiProjectDashboardChartInfo(bidNumber: string): Observable<any> { return this.http.get<any>(this.baseUrl + 'multiProjectDashboardChartInfo/' + encodeURI(bidNumber)); }
  getMultiProjectTasks(bidNumber: string): Observable<any> { return this.http.get<any>(this.baseUrl + 'multiProjectTasks/' + encodeURI(bidNumber)); }
  getMultiProjectTeamMembers(bidNumber: string): Observable<any> { return this.http.get<any>(this.baseUrl + 'multiProjectTeamMembers/' + encodeURI(bidNumber)); }

  getProjectInfoSummary(bidNumber: string): Observable<any> { return this.http.get<any>(this.baseUrl + 'info/summary/' + encodeURI(bidNumber)); }

  filterProjectActions(auth: AuthService, project: Project, actions: Array<{ label: string, type: string}>): Array<{ label: string, type: string }> {
    if( !project || !actions || actions.length === 0) {
      return actions;
    }
    const projectIsLive = (project.starts ?? 0) > 0 || project.projectStatus == 'Full Launch' || project.projectStatus == 'Soft Launch'; ;
    if(!auth.isSuperAdmin() && projectIsLive) {
      return actions.filter(e => e.type !== 'delete');
    }
    return actions;
  }

  getProjectTeamMembersForPartners(projectId: string, partnerIds: string[]): Observable<ProjectTeamMembersForPartner[]> {
    return this.http.post<ProjectTeamMembersForPartner[]> (this.baseUrl + 'getProjectTeamMembersForPartners/' + projectId, partnerIds);
  }

  reconciliationEmailNotification(projectId: string, data: { from: string; to: string[]; subject: string; body: string; vendorName: string }): Observable<any> {
    return this.http.post(this.baseUrl + 'reconciliation/' + projectId + '/notifyvendor', data);
  }
}
